import React, { useState } from 'react';

import FaUser from 'react-icons/lib/fa/user';

import { Collapse } from 'wanderu-animations';
import A from 'wanderu-component-react-router-link';
import {
    Button,
    CaretIcon,
    Checkbox,
    ElementQuery,
    Lightbox,
    Modal,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import Events from './events/TravelerInfoEvents';
import {
    TRAVELER_TYPE,
    WHEELCHAIR_TYPE,
} from '../../../constants';

import styles from './TravelerInfo.scss';
import breakpoints from 'wanderu-css/mediaQueries.scss';

import type { TFunction } from 'i18next';

interface ScreenSizeQueries {
    isMediumScreenOrBelow: boolean;
}

interface TravelerInfoProps {
    itineraryId: string;
    maxSeats: number;
    maxWheelchairs: number;
    minSeats: number;
    minWheelchairs: number;
    nonAdaCarriers: string[];
    requestedWheelchairs: number;
    seats: number;
    setTravelerInfo: (info: { seats?: number; wheelchairs?: number }) => void;
    t: TFunction;
    wheelchairs: number;
}

const SIZE_QUERY = {
    isMediumScreenOrBelow: {
        maxWidth: breakpoints.maxWidthMediumScreen,
    },
};

const TravelerInfo: React.FC<TravelerInfoProps> = ({
    itineraryId = '',
    maxSeats = 1,
    maxWheelchairs = 0,
    minSeats = 1,
    minWheelchairs = 0,
    nonAdaCarriers = [],
    requestedWheelchairs = 0,
    seats,
    setTravelerInfo,
    t,
    wheelchairs,
}) => {
    const [isMediumScreenOrBelow, setIsMediumScreenOrBelow] = useState(false);
    const [isOpen, setIsOpen] = useState(seats > 1);
    const [showWheelchair, setShowWheelchair] = useState(requestedWheelchairs > 0);

    const onChangeScreenSize = (queries: ScreenSizeQueries) => {
        if (queries.isMediumScreenOrBelow === isMediumScreenOrBelow) {
            return;
        }

        setIsMediumScreenOrBelow(queries.isMediumScreenOrBelow);
    };

    const toggleRow = () => {
        Events.addTravelerDropdownClicked({
            itineraryId,
            travelers: seats,
            wheelchairs,
        });
        setIsOpen(!isOpen);
    };

    const onSeatChange = (newSeats: number, { increase }: { increase: boolean }) => {
        Events.updateTravelerButtonClicked({ increase, itineraryId, travelers: newSeats });
    };

    const onWheelchairChange = (newWheelchairs: number, { increase }: { increase: boolean }) => {
        Events.updateWheelchairSeatButtonClicked({
            increase,
            itineraryId,
            wheelchairs: newWheelchairs,
        });
    };

    const increaseTravelers = () => {
        const newSeats = seats + 1;
        setTravelerInfo?.({ seats: newSeats });
        onSeatChange(newSeats, { increase: true });
    };

    const decreaseTravelers = () => {
        const newSeats = seats - 1;
        setTravelerInfo?.({ seats: newSeats });
        onSeatChange(newSeats, { increase: false });
    };

    const increaseWheelchairs = () => {
        if (wheelchairs >= seats) {
            return;
        }

        const newWheelchairs = wheelchairs + 1;
        setTravelerInfo?.({ wheelchairs: newWheelchairs });
        onWheelchairChange(newWheelchairs, { increase: true });
    };

    const decreaseWheelchairs = () => {
        if (wheelchairs === 0) {
            return;
        }

        const newWheelchairs = wheelchairs - 1;
        setTravelerInfo?.({ wheelchairs: wheelchairs - 1 });
        onWheelchairChange(newWheelchairs, { increase: false });
    };

    const resetWheelchairs = () => {
        if (wheelchairs === 0) {
            return;
        }

        setTravelerInfo?.({ wheelchairs: 0 });
    };

    const toggleWheelchair = (isActive: boolean) => {
        if (isActive && wheelchairs === 0 && maxWheelchairs > 0) {
            increaseWheelchairs();
        }

        if (!isActive && wheelchairs !== 0) {
            resetWheelchairs();
        }

        setShowWheelchair(isActive);
    };

    const decreaseTravelersAndWheelchairs = () => {
        const newSeats = seats - 1;
        setTravelerInfo?.({
            seats: newSeats,
            wheelchairs: wheelchairs - 1,
        });
        onSeatChange(newSeats, { increase: false });
    };

    const decreasePassengerCount = (e: React.MouseEvent<HTMLButtonElement>) => {
        const { type } = (e.target as HTMLButtonElement).dataset;

        if (type === WHEELCHAIR_TYPE) {
            return decreaseWheelchairs();
        }

        if (seats === wheelchairs) {
            return decreaseTravelersAndWheelchairs();
        }

        return decreaseTravelers();
    };

    const increasePassengerCount = (e: React.MouseEvent<HTMLButtonElement>) => {
        const { type } = (e.target as HTMLButtonElement).dataset;

        if (type === WHEELCHAIR_TYPE) {
            return increaseWheelchairs();
        }

        return increaseTravelers();
    };

    const renderTravelerRow = (type: string, count: number, max: number, min: number) => {
        const disableAdd = count === max;
        const disableSubtract = count === min;

        return (
            <div className={styles.travelerRow}>
                <span className={styles.travelerType}>
                    {t<string>(type, { count })}
                </span>

                <div className={styles.counter}>
                    {/*
                        uses the native button element because our component
                        version won’t handle e.target.dataset (see increasePassengerCount)
                    */}
                    <button
                        aria-label={t('less')}
                        className={styles.minusIcon}
                        data-type={type}
                        disabled={disableSubtract}
                        onClick={decreasePassengerCount}
                    />

                    <span className={`count ${type}`}>
                        {count}
                    </span>

                    <button
                        aria-label={t('more')}
                        className={styles.plusIcon}
                        data-type={type}
                        disabled={disableAdd}
                        onClick={increasePassengerCount}
                    />
                </div>
            </div>
        );
    };

    const renderSeatRow = () => {
        return renderTravelerRow(TRAVELER_TYPE, seats, maxSeats, minSeats);
    };

    const renderWheelchairRow = () => {
        const max = Math.min(seats, maxWheelchairs);
        return renderTravelerRow(WHEELCHAIR_TYPE, wheelchairs, max, minWheelchairs);
    };

    const renderPassengerContainer = () => {
        let carrierNames = nonAdaCarriers[0];
        if (nonAdaCarriers.length > 1) {
            const lastCarrier = nonAdaCarriers.pop();
            carrierNames = `${nonAdaCarriers.join(', ')} ${t('or')} ${lastCarrier}`;
        }

        return (
            <div className={styles.container}>
                {renderSeatRow()}
                {showWheelchair && renderWheelchairRow()}
                <div className={styles.wheelchairCheckbox}>
                    <Checkbox
                        checkboxClassName={styles.checkbox}
                        checked={showWheelchair}
                        labelText={t('need-wheelchair')}
                        onChange={toggleWheelchair}
                    />
                </div>
                {showWheelchair && !!nonAdaCarriers.length && (
                    <div className={styles.wheelchairDisclaimer}>
                        {t<string>('wheelchair-disclaimer', {
                            carrierName: (
                                <A
                                    href="//www.wanderu.com/carrier/"
                                    key="wheelchairDisclaimer"
                                    native
                                >
                                    {carrierNames}
                                </A>
                            ),
                        })}
                    </div>
                )}
            </div>
        );
    };

    const queryElement = typeof window !== 'undefined'
        ? document.body
        : null;

    return (
        <ElementQuery
            element={queryElement}
            onChange={onChangeScreenSize}
            passAsProps={false}
            queries={SIZE_QUERY}
        >
            <div className={styles.travelerInfoTab}>
                <Button
                    className={styles.toggleRow}
                    onClick={toggleRow}
                    type="button"
                    unstyled
                >
                    <div className={styles.titleRow}>
                        {seats > 1
                            ? (
                                <span className={styles.travelerCount}>
                                    {seats}
                                </span>
                            )
                            : <FaUser className={styles.travelerIcon} />}
                        <span className={styles.title}>{t<string>('add-traveler')}</span>
                    </div>

                    <CaretIcon showUpArrow={isOpen} />
                </Button>

                <Collapse
                    className={styles.collapse}
                    duration={500}
                    isOpen={isOpen}
                >
                    {renderPassengerContainer()}
                </Collapse>
                {isMediumScreenOrBelow && isOpen && (
                    <Modal className={styles.mobileModal}>
                        <Lightbox
                            className={styles.mobileContainer}
                            innerClassName={styles.mobileContainerInner}
                            onHide={toggleRow}
                        >
                            {renderPassengerContainer()}
                        </Lightbox>
                    </Modal>
                )}
            </div>
        </ElementQuery>
    );
};

export { TravelerInfo };
export default localize(TravelerInfo, ['t'], 'baseApp');
